import { useState } from 'react'
import { templateGallery } from './templates'
import type { Locale } from './types'

/*
 * Galeria de sabloane din /app/reports, pe grupe de module. Se vad numai
 * grupele cu modulul activ pe compania aleasa; un sablon fara niciun bloc vazut
 * (modul inactiv sau fara drept) sta in grupa lui, oprit, cu motivul scris pe
 * cartonas. Casuta de cautare trece prin nume, descriere si titlurile blocurilor.
 */

interface Props {
  modules: readonly string[]
  visible: ReadonlySet<string>
  locale: Locale
  onPick: (key: string) => void
}

export function TemplateGallery({ modules, visible, locale, onPick }: Props) {
  const [query, setQuery] = useState('')
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const blocks = (n: number) => t(`${n} ${n === 1 ? 'bloc' : 'blocuri'}`, `${n} ${n === 1 ? 'block' : 'blocks'}`)
  const sections = templateGallery(modules, visible, query)
  const noModules = templateGallery(modules, visible).length === 0

  return (
    <>
      <div className="od-rep-section-head">
        <h3>{t('Sabloane', 'Templates')}</h3>
        <span className="od-rep-meta">{t('Un sablon pune mai multe blocuri in raport; le poti schimba sau scoate pe oricare.', 'A template puts several blocks in your report; you can change or remove any of them.')}</span>
      </div>
      {noModules ? (
        <div className="od-rep-empty od-rep-template-empty">
          {t('Niciun modul nu e activ pe aceasta companie, deci nu exista sabloane de aratat.', 'No module is active for this company, so there are no templates to show.')}
        </div>
      ) : (
        <>
          <input
            type="search"
            className="form-control input-sm od-rep-template-search"
            placeholder={t('Cauta un sablon…', 'Search templates…')}
            aria-label={t('Cauta un sablon', 'Search templates')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {sections.length === 0 ? (
            <div className="od-rep-empty od-rep-template-empty">{t(`Niciun sablon nu se potriveste cu „${query.trim()}”.`, `No template matches “${query.trim()}”.`)}</div>
          ) : null}
          {sections.map(({ group, cards }) => (
            <section key={group.key} className="od-rep-template-group" data-group={group.key}>
              <div className="od-rep-template-group-head">
                <h4>{group.label[locale]}</h4>
                <span className="od-rep-template-count">{cards.length}</span>
                <span className="od-rep-template-group-about">{group.about[locale]}</span>
              </div>
              <div className="od-rep-template-grid">
                {cards.map(({ template: x, usable, disabled }) => (
                  <button
                    key={x.key}
                    type="button"
                    className={`od-rep-template${disabled ? ' od-rep-template-off' : ''}`}
                    data-template={x.key}
                    disabled={disabled !== null}
                    title={disabled ? disabled[locale] : undefined}
                    onClick={() => onPick(x.key)}
                  >
                    <span className="od-rep-template-name">{x.label[locale]}</span>
                    <span className="od-rep-template-about">{x.about[locale]}</span>
                    <span className="od-rep-template-meta">
                      {disabled
                        ? disabled[locale]
                        : usable === x.blocks.length
                          ? blocks(usable)
                          : t(`${usable} din ${x.blocks.length} blocuri`, `${usable} of ${x.blocks.length} blocks`)}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </>
  )
}
